import React from 'react';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * A single editable magazine field: shows the stored value, swaps to a TextField
 * while it is the actively edited field, and exposes edit / copy / clear
 * (and save / cancel while editing) actions.
 */
const MagazineFieldEditor = ({
  label,
  labelColor,
  capitalizeLabel = false,
  isEditing,
  value,
  tempValue,
  setTempValue,
  rows,
  inputSx,
  displayBg,
  displayMinHeight,
  labelledActions = false,
  disabled,
  placeholder,
  onSave,
  onCancel,
  onEdit,
  onCopy,
  onClear
}) => (
  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255,255,255,0.05)' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: labelColor,
            fontWeight: 600,
            mb: 1,
            display: 'block',
            ...(capitalizeLabel ? { textTransform: 'capitalize' } : {})
          }}
        >
          {label}
        </Typography>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            rows={rows}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            sx={inputSx}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              wordBreak: 'break-all',
              backgroundColor: displayBg,
              p: 1.5,
              borderRadius: 1,
              minHeight: displayMinHeight,
              fontFamily: 'monospace',
              fontSize: '0.85rem'
            }}
          >
            {value || '(empty)'}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isEditing ? (
          <>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={labelledActions ? <SaveIcon /> : undefined}
              onClick={onSave}
              disabled={disabled}
              sx={labelledActions ? { minWidth: '80px' } : { minWidth: '40px', p: 1 }}
            >
              {labelledActions ? 'Save' : <SaveIcon />}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={labelledActions ? <ClearIcon /> : undefined}
              onClick={onCancel}
              sx={labelledActions ? { minWidth: '80px' } : { minWidth: '40px', p: 1 }}
            >
              {labelledActions ? 'Cancel' : <ClearIcon />}
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onEdit}
              sx={{ minWidth: '40px', p: 1 }}
            >
              <EditIcon />
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={onCopy}
              sx={{ minWidth: '40px', p: 1 }}
            >
              <ContentCopyIcon />
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={onClear}
              disabled={disabled}
              sx={{ minWidth: '40px', p: 1 }}
            >
              <DeleteIcon />
            </Button>
          </>
        )}
      </Box>
    </Box>
  </Paper>
);

export default MagazineFieldEditor;
